// src/api/useNotifications.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Fetch notifications list
export const useNotifications = (token, params = {}) => {
  const queryKey = ['notifications', params];
  const { 
    enabled = true, 
    staleTime = 30000, // 30 seconds
    cacheTime = 300000, // 5 minutes
    refetchOnWindowFocus = false,
    refetchOnMount = true,
    refetchOnReconnect = false,
    ...queryParams 
  } = params;

  const fetch = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const qs = new URLSearchParams(queryParams).toString();
    const url = `${API_BASE}/notifications${qs ? `?${qs}` : ''}`;
    console.log('Fetching notifications from:', url);
    const { data } = await axios.get(url, { headers });
    return data;
  };

  return useQuery({ 
    queryKey, 
    queryFn: fetch, 
    enabled: enabled && !!token,
    staleTime,
    cacheTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchOnReconnect
  });
};

// Fetch unread count
export const useUnreadCount = (token) => {
  return useQuery({
    queryKey: ['notifications', 'unreadCount'],
    enabled: !!token,
    queryFn: async () => {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const url = `${API_BASE}/notifications/unread/count`;
      console.log('Fetching unread count from:', url);
      const { data } = await axios.get(url, { headers });
      return data.count;
    },
    // Add caching to reduce API calls
    staleTime: 30000, // 30 seconds
    cacheTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });
};

// Mutation: mark single notification as read
export const useMarkAsRead = (token) => {
  const qc = useQueryClient();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  return useMutation({
    mutationFn: (id) => {
      const url = `${API_BASE}/notifications/${id}/read`;
      console.log('Marking notification as read:', url);
      return axios.put(url, null, { headers });
    },
    onSuccess: () => {
      console.log('Successfully marked notification as read');
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'unreadCount'] });
    },
    onError: (error) => {
      console.error('Error marking notification as read:', error);
    }
  });
};

// Mutation: mark all notifications as read
export const useMarkAllRead = (token) => {
  const qc = useQueryClient();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  
  return useMutation({
    mutationFn: () => {
      const url = `${API_BASE}/notifications/all/read`;
      console.log('Marking all notifications as read:', url);
      return axios.put(url, null, { headers });
    },
    onSuccess: () => {
      console.log('Successfully marked all notifications as read');
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'unreadCount'] });
    },
    onError: (error) => {
      console.error('Error marking all notifications as read:', error);
      console.error('Error details:', error.response?.data);
    }
  });
};

// Mutation: delete notification
export const useDeleteNotification = (token) => {
  const qc = useQueryClient();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  return useMutation({
    mutationFn: (id) => {
      const url = `${API_BASE}/notifications/${id}`;
      console.log('Deleting notification:', url);
      return axios.delete(url, { headers });
    },
    onSuccess: () => {
      console.log('Successfully deleted notification');
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'unreadCount'] });
    },
    onError: (error) => {
      console.error('Error deleting notification:', error);
    }
  });
}; 